import { useEffect, useState } from 'react';
import { Bar, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Info, X, ArrowDown, ArrowUp } from 'lucide-react';

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

function getUserInitials(user: any): string {
    const name = user.display_name || user.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    return user.id.slice(0, 2).toUpperCase();
}

function getUserDisplayName(user: any): string {
    return user.display_name || user.email || user.id.slice(0, 8);
}

/**
 * HQ admin dashboard displaying high-level performance metrics.
 *
 * Fetches aggregate data from `GET /api/v1/hq/metrics` on mount, including:
 * - Total users and transactions
 * - All-time income and expense volumes
 * - 30-day daily time-series (transactions + unique active users per day)
 *
 * Requires HQ admin authentication (enforced server-side).
 */
export default function HQDashboard() {
    const { getToken, logout, currentUser } = useAuth();
    const [metrics, setMetrics] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Active users drawer state
    const [selectedMetric, setSelectedMetric] = useState<'dau' | 'wau' | 'mau' | null>(null);
    const [activeUsers, setActiveUsers] = useState<any[]>([]);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [drawerError, setDrawerError] = useState('');

    const [sortBy, setSortBy] = useState<'last_active' | 'created_at'>('last_active');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const sortedActiveUsers = [...activeUsers].sort((a, b) => {
        const valA = new Date(a[sortBy] || 0).getTime();
        const valB = new Date(b[sortBy] || 0).getTime();
        return sortOrder === 'desc' ? valB - valA : valA - valB;
    });

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const token = await getToken();
                if (!token) return;

                const res = await fetch('/api/v1/hq/metrics', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const json = await res.json();

                if (!res.ok || !json.success) {
                    throw new Error(json.error || 'Failed to fetch metrics');
                }

                setMetrics(json.data);
            } catch (err: any) {
                console.error('HQDashboard.fetchMetrics Error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, [getToken]);

    useEffect(() => {
        if (!selectedMetric) return;

        const fetchActiveUsers = async () => {
            setDrawerLoading(true);
            setDrawerError('');
            try {
                const token = await getToken();
                if (!token) return;

                const res = await fetch(`/api/v1/hq/metrics/active-users?timeframe=${selectedMetric}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const json = await res.json();
                if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch active users');

                setActiveUsers(json.data);
            } catch (err: any) {
                console.error('HQDashboard.fetchActiveUsers Error:', err);
                setDrawerError(err.message);
            } finally {
                setDrawerLoading(false);
            }
        };

        fetchActiveUsers();
    }, [selectedMetric, getToken]);

    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
            {/* Sidebar */}
            <div className="w-64 border-r border-zinc-800 p-6 flex flex-col gap-8">
                <div className="font-bold text-xl text-yellow-400">HQ AppPulse</div>

                <nav className="flex flex-col gap-2 flex-grow">
                    <a href="#" className="flex items-center gap-3 px-4 py-3 bg-zinc-900 text-yellow-400 rounded-xl font-medium">
                        Dashboard
                    </a>
                    <Link to="/hq/insights" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        User Insights
                    </Link>
                    <Link to="/hq/transactions" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Transactions
                    </Link>
                    <Link to="/hq/notifications" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Notifications
                    </Link>
                    <a href="#" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
                        Reports
                    </a>
                </nav>

                <div className="flex flex-col gap-4 border-t border-zinc-800 pt-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700"></div>
                        <div className="overflow-hidden">
                            <div className="text-sm font-bold truncate max-w-[120px]">{currentUser?.email}</div>
                            <div className="text-xs text-zinc-500">HQ Access</div>
                        </div>
                    </div>
                    <button onClick={logout} className="text-sm text-left text-zinc-500 hover:text-red-400 transition-colors px-2">
                        Sign out
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-10 overflow-y-auto">
                <h1 className="text-3xl font-bold mb-8">Performance Overview</h1>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl mb-8">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="text-zinc-500">Loading metrics...</div>
                ) : (
                    <>
                        {/* KPI Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                            {[
                                { label: 'Total Users', value: metrics?.totalUsers?.toLocaleString() || 0 },
                                { label: 'Total Transactions', value: metrics?.totalTransactions?.toLocaleString() || 0 },
                                { label: 'Total Income Vol', value: `₹${metrics?.totalIncome?.toLocaleString() || 0}` },
                                { label: 'Total Expense Vol', value: `₹${metrics?.totalExpense?.toLocaleString() || 0}` }
                            ].map((kpi, idx) => (
                                <div key={idx} className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl shadow-sm">
                                    <div className="text-zinc-400 text-sm mb-2">{kpi.label}</div>
                                    <div className="text-3xl font-bold text-white">{kpi.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Active User KPIs */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                            {[
                                { 
                                    id: 'dau' as const,
                                    label: 'Daily Active Users (DAU)', 
                                    value: metrics?.DAU?.toLocaleString() || 0,
                                    tooltip: 'Unique users with transactions since 00:00 today'
                                },
                                { 
                                    id: 'wau' as const,
                                    label: 'Weekly Active Users (WAU)', 
                                    value: metrics?.WAU?.toLocaleString() || 0,
                                    tooltip: 'Unique users with transactions in the last 7 days'
                                },
                                { 
                                    id: 'mau' as const,
                                    label: 'Monthly Active Users (MAU)', 
                                    value: metrics?.MAU?.toLocaleString() || 0,
                                    tooltip: 'Unique users with transactions in the last 30 days'
                                }
                            ].map((kpi) => (
                                <div 
                                    key={kpi.id} 
                                    onClick={() => setSelectedMetric(kpi.id)}
                                    className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl shadow-sm cursor-pointer hover:bg-zinc-800/80 border-t-2 border-t-transparent hover:border-t-yellow-400/50 transition-all"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="text-zinc-400 text-sm">{kpi.label}</div>
                                        <div className="group relative flex items-center justify-center">
                                            <Info className="w-4 h-4 text-zinc-500 cursor-help hover:text-zinc-300 transition-colors" />
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-200 text-center shadow-xl z-10 pointer-events-none">
                                                {kpi.tooltip}
                                                <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-zinc-800" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-white">{kpi.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Charts block placeholder */}
                        <div className="grid lg:grid-cols-2 gap-8">
                            <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl flex flex-col justify-center">
                                <h2 className="text-xl font-bold mb-1">Daily Active Users vs. Transactions</h2>
                                <p className="text-zinc-500 text-sm mb-6">Performance over the last 30 days</p>

                                {metrics?.dailyData && metrics.dailyData.length > 0 ? (
                                    <div className="h-64 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={metrics.dailyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <XAxis
                                                    dataKey="date"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: '#71717a', fontSize: 12 }}
                                                    dy={10}
                                                    tickFormatter={(tick, index) => {
                                                        // Show roughly every 7 days based on the 30-day length
                                                        return index % 7 === 0 || index === metrics.dailyData.length - 1 ? tick : '';
                                                    }}
                                                />
                                                <YAxis
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: '#71717a', fontSize: 12 }}
                                                    dx={-10}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: '#27272a' }}
                                                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#f4f4f5' }}
                                                    itemStyle={{ color: '#f4f4f5' }}
                                                />
                                                <Legend
                                                    iconType="circle"
                                                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                                                />
                                                <Bar dataKey="dau" name="DAU" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                                <Bar dataKey="transactions" name="Transactions" fill="#93c5fd" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="h-64 w-full flex items-center justify-center text-zinc-500 italic">
                                        No daily data available
                                    </div>
                                )}
                            </div>
                            <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl h-80 flex flex-col justify-center text-zinc-500">
                                <h2 className="text-xl font-bold mb-1 text-zinc-100">Builds in the wild</h2>
                                <p className="text-zinc-500 text-sm mb-6">Users grouped by native app version and OTA bundle</p>

                                {metrics?.appVersionDistribution && metrics.appVersionDistribution.length > 0 ? (
                                    <div className="h-64 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart
                                                data={metrics.appVersionDistribution}
                                                layout="vertical"
                                                margin={{ top: 10, right: 20, left: 28, bottom: 0 }}
                                            >
                                                <XAxis
                                                    type="number"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: '#71717a', fontSize: 12 }}
                                                    allowDecimals={false}
                                                />
                                                <YAxis
                                                    dataKey="label"
                                                    type="category"
                                                    width={132}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={{ fill: '#a1a1aa', fontSize: 12 }}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: '#27272a' }}
                                                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#f4f4f5' }}
                                                    itemStyle={{ color: '#f4f4f5' }}
                                                />
                                                <Bar dataKey="users" name="Users" fill="#facc15" radius={[0, 4, 4, 0]} maxBarSize={28} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="h-64 w-full flex items-center justify-center italic">
                                        No build data available
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Active Users Drawer */}
            {selectedMetric && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-40 transition-opacity"
                        onClick={() => setSelectedMetric(null)}
                    />

                    {/* Panel */}
                    <div className="fixed top-0 right-0 h-full w-full max-w-sm sm:max-w-md bg-zinc-900 border-l border-zinc-800 z-50 flex flex-col shadow-2xl transition-transform">
                        {/* Drawer header */}
                        <div className="flex items-start justify-between p-6 border-b border-zinc-800 bg-zinc-900 z-10 sticky top-0">
                            <div>
                                <div className="text-xl font-bold text-zinc-100 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 flex items-center justify-center">
                                        {selectedMetric === 'dau' ? '1D' : selectedMetric === 'wau' ? '7D' : '30D'}
                                    </div>
                                    {selectedMetric === 'dau' ? 'Daily Active Users' : selectedMetric === 'wau' ? 'Weekly Active Users' : 'Monthly Active Users'}
                                </div>
                                <div className="text-sm text-zinc-500 mt-2">
                                    {drawerLoading ? 'Searching users...' : `${activeUsers.length} active users`}
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedMetric(null)}
                                className="p-2 -mr-2 -mt-2 text-zinc-500 hover:bg-zinc-800/50 hover:text-zinc-100 rounded-xl transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        {/* Sort Controls */}
                        {!drawerLoading && activeUsers.length > 0 && (
                            <div className="px-6 py-3 border-b border-zinc-800 bg-zinc-900/50 flex justify-between items-center text-sm z-10 sticky top-[97px]">
                                <div className="text-zinc-400">Sort by:</div>
                                <div className="flex gap-2">
                                    <select 
                                        className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-lg px-2 py-1 outline-none focus:border-yellow-400 hover:border-zinc-600 transition-colors"
                                        value={sortBy} 
                                        onChange={(e) => setSortBy(e.target.value as any)}
                                    >
                                        <option value="last_active">Last Active</option>
                                        <option value="created_at">Join Date</option>
                                    </select>
                                    <button 
                                        onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                        className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-lg flex items-center justify-center p-1.5 hover:bg-zinc-700 transition-colors"
                                        title={`Sort ${sortOrder === 'desc' ? 'Descending' : 'Ascending'}`}
                                    >
                                        {sortOrder === 'desc' ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto">
                            {drawerError && (
                                <div className="mx-6 mt-6 bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl text-sm">
                                    {drawerError}
                                </div>
                            )}

                            {drawerLoading ? (
                                <div className="divide-y divide-zinc-800 px-2 mt-2">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
                                            <div className="w-10 h-10 rounded-full bg-zinc-800 flex-shrink-0" />
                                            <div className="flex-1">
                                                <div className="h-4 bg-zinc-800 rounded w-1/2 mb-2" />
                                                <div className="h-3 bg-zinc-800 rounded w-1/3" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : !activeUsers.length ? (
                                <div className="p-12 text-center text-zinc-500 italic text-sm">
                                    No users found for this timeframe
                                </div>
                            ) : (
                                <div className="divide-y divide-zinc-800 px-2 mt-2 pb-6">
                                    {sortedActiveUsers.map((user) => (
                                        <div key={user.id} className="p-4 flex items-center gap-4 hover:bg-zinc-800/50 rounded-xl transition-colors">
                                            <div className="w-10 h-10 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 flex items-center justify-center font-bold text-sm flex-shrink-0">
                                                {getUserInitials(user)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-bold text-zinc-200 truncate">
                                                    {getUserDisplayName(user)}
                                                </div>
                                                {user.display_name && user.email && (
                                                    <div className="text-xs text-zinc-500 truncate mt-0.5">
                                                        {user.email}
                                                    </div>
                                                )}
                                                <div className="text-xs text-zinc-500 mt-1">
                                                    Last Active: {user.last_active ? formatDate(user.last_active) : 'N/A'} • Joined {formatDate(user.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
